// M1 THROWAWAY (OPTI-6761): pre-flight snapshot for the Live Analytics chat
// system prompt. Issues batched calls to the prototype
// `/analytics/api/v1/query` endpoint (workspace-services PR #12620) and formats
// the per-conversation context the agent receives as `structured_input`: the top
// `strings`/`numerics` MAP keys for the flow, and the flow versions seen
// recently. Delete once the proper agent lifecycle lands.

import { chunkQueries } from "src/analytics/metrics/queryBatching";
import { analyticsQueryApi } from "src/api/endpoints";
import type { ResultsValue } from "src/clients/analytics-query";
import type {
  FlowVersionEntry,
  SchemaKeyEntry,
  WorkspaceSchema,
} from "src/flow/analytics/m1/liveAnalyticsAgentContext";

const FLOW_ID_PATTERN = /^[0-9a-fA-F-]{36}$/;

// A `strings` key with more distinct values than this (in the sampled rows)
// reads as an id / free-text column rather than an enum — omit it and let the
// agent sample on demand.
const ENUM_KEY_MAX_CARDINALITY = 50;
// Show at most this many of the most-frequent values per enum-like key; a key
// with more distinct values renders as truncated.
const VALUE_DISPLAY_LIMIT = 20;
// Recent-window bound on the MAP-sampling sub-selects. `flow_id` is not the
// partition key, so without a `start_datetime` predicate the sample sub-select
// full-scans the whole Iceberg table to sort — driving the analytics query
// Lambda toward its memory cap (OPTI-10157). Bounding to a recent window prunes
// to those day-partitions.
const SAMPLE_WINDOW_DAYS = 30;

// Also spelled out by the `decisions_last_7d` field name, in the prompt and the
// agent input schema.
const VOLUME_WINDOW_DAYS = 7;

// Reaches the agent verbatim; the prompt keys its discovery rule off this field.
const UNSAMPLED = `no output keys in the last ${SAMPLE_WINDOW_DAYS} days, so this flow's keys are unknown`;

const resultRows = (
  slice: ResultsValue | undefined,
): ReadonlyArray<ReadonlyArray<unknown>> =>
  slice && !("error" in slice) ? slice.rows : [];

export type PreflightContext = {
  workspaceSchema: WorkspaceSchema;
  flowVersions: FlowVersionEntry[];
};

// The preflight samples this many most-recent decisions per MAP-key query.
const SAMPLED_DECISIONS = 5000;

const sanitizeFlowKey = (flowId: string): string =>
  `f${flowId.replace(/[^a-zA-Z0-9]/g, "")}`;

const keyEntries = (
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
): SchemaKeyEntry[] =>
  rows.map((r) => ({ key: String(r[0]), decisions: Number(r[1]) }));

// Group (key, value, count, distinct_n) rows per key. `distinctValues` above the
// number of values carried is what tells the agent it has a top-N.
const valuesByKey = (
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
): Map<string, Pick<SchemaKeyEntry, "values" | "distinct_values">> =>
  rows.reduce<Map<string, Pick<SchemaKeyEntry, "values" | "distinct_values">>>(
    (acc, r) => {
      const key = String(r[0]);
      const entry = acc.get(key) ?? {
        values: [],
        distinct_values: Number(r[3]),
      };
      entry.values?.push({ value: String(r[1]), decisions: Number(r[2]) });
      acc.set(key, entry);
      return acc;
    },
    new Map(),
  );

const buildWorkspaceSchema = (
  volumeRows: ReadonlyArray<ReadonlyArray<unknown>>,
  stringRows: ReadonlyArray<ReadonlyArray<unknown>>,
  numericRows: ReadonlyArray<ReadonlyArray<unknown>>,
  booleanRows: ReadonlyArray<ReadonlyArray<unknown>>,
  stringValueRows: ReadonlyArray<ReadonlyArray<unknown>>,
): WorkspaceSchema => {
  const volume = Number(volumeRows[0]?.[0] ?? 0);

  const sampledValues = valuesByKey(stringValueRows);
  const counted = keyEntries(stringRows).map((entry) => ({
    ...entry,
    ...(sampledValues.get(entry.key) ?? {}),
  }));
  // The value query ranks low-cardinality keys on their own, so an enum key can
  // place there while ids crowd it out of the top-40 key list. Carry it with its
  // values but no `decisions` — the key query never counted it.
  const countedKeys = new Set(counted.map((entry) => entry.key));
  const uncounted = Array.from(sampledValues.entries())
    .filter(([key]) => !countedKeys.has(key))
    .map(([key, entry]) => ({ key, ...entry }));

  const strings = [...counted, ...uncounted];
  const numerics = keyEntries(numericRows);
  const booleans = keyEntries(booleanRows);
  // Empty lists alone would read as "this flow has no keys".
  const unavailable =
    strings.length === 0 && numerics.length === 0 && booleans.length === 0
      ? { unavailable: UNSAMPLED }
      : {};
  return {
    decisions_last_7d: volume,
    sampled_decisions: SAMPLED_DECISIONS,
    strings,
    numerics,
    booleans,
    ...unavailable,
  };
};

// `flow_data` only stores `flow_version_id` (a UUID); the human-readable
// version name lives in the control plane, so an unmapped id carries `null`.
const buildFlowVersions = (
  rows: ReadonlyArray<ReadonlyArray<unknown>>,
  versionIdToName: Record<string, string>,
): FlowVersionEntry[] =>
  rows.map((r) => ({
    flow_version_id: String(r[0]),
    name: versionIdToName[String(r[0])] ?? null,
    decisions_30d: Number(r[1]),
  }));

export const fetchPreflightBlocks = async (
  baseUrl: string,
  flowId: string,
  versionIdToName: Record<string, string>,
): Promise<PreflightContext> => {
  // Hard-reject anything that isn't a UUID before string-interpolating into
  // SQL. The metrics endpoint is auth-gated, but breaking the query on a
  // weird flowId would still produce a confusing prompt rather than a clean
  // fallback.
  if (!FLOW_ID_PATTERN.test(flowId)) {
    throw new Error(`Invalid flowId for preflight snapshot: ${flowId}`);
  }

  const safe = sanitizeFlowKey(flowId);

  const queries = {
    volume: `SELECT COUNT(*) AS n FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '${VOLUME_WINDOW_DAYS} days'`,
    [`s_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT strings FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '${SAMPLE_WINDOW_DAYS} days' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_keys(sample.strings)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
    [`n_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT numerics FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '${SAMPLE_WINDOW_DAYS} days' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_keys(sample.numerics)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
    [`b_${safe}`]: `SELECT key, COUNT(*) AS n FROM (SELECT booleans FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '${SAMPLE_WINDOW_DAYS} days' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_keys(sample.booleans)) AS t(key) GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
    // Top values per enum-like `strings` key, with each key's distinct count so
    // the renderer can flag a truncated (top-N) list.
    [`v_${safe}`]: `WITH pairs AS (SELECT entry.key AS k, entry.value AS v, COUNT(*) AS n FROM (SELECT strings FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '${SAMPLE_WINDOW_DAYS} days' ORDER BY start_datetime DESC LIMIT 5000) sample, UNNEST(map_entries(sample.strings)) AS t(entry) GROUP BY 1, 2), card AS (SELECT k, COUNT(*) AS distinct_n, SUM(n) AS total_n FROM pairs GROUP BY k), enum_keys AS (SELECT k, distinct_n FROM card WHERE distinct_n <= ${ENUM_KEY_MAX_CARDINALITY} ORDER BY total_n DESC LIMIT 40), ranked AS (SELECT p.k, p.v, p.n, e.distinct_n, ROW_NUMBER() OVER (PARTITION BY p.k ORDER BY p.n DESC) AS rn FROM pairs p JOIN enum_keys e USING (k)) SELECT k, v, n, distinct_n FROM ranked WHERE rn <= ${VALUE_DISPLAY_LIMIT} ORDER BY k, n DESC`,
    versions: `SELECT flow_version_id, COUNT(*) AS n FROM flow_data WHERE flow_id = '${flowId}' AND start_datetime >= CURRENT_TIMESTAMP - INTERVAL '30 days' AND flow_version_id IS NOT NULL GROUP BY 1 ORDER BY 2 DESC LIMIT 40`,
  };

  const responses = await Promise.allSettled(
    chunkQueries(queries).map((batch) =>
      analyticsQueryApi(baseUrl).querySqlAnalyticsApiV1QueryPost({
        queryRequest: { queries: batch },
      }),
    ),
  );
  const results = Object.fromEntries(
    responses.flatMap((response) =>
      response.status === "fulfilled"
        ? Object.entries(response.value.data.results ?? {})
        : [],
    ),
  );

  // Without the volume gate an empty sample is indistinguishable from a flow
  // with no decisions, so a chunk carrying it away is a hard failure.
  if (results.volume === undefined) {
    const rejected = responses.find(
      (response): response is PromiseRejectedResult =>
        response.status === "rejected",
    );
    throw rejected?.reason instanceof Error
      ? rejected.reason
      : new Error("Preflight returned no decision volume");
  }

  return {
    workspaceSchema: buildWorkspaceSchema(
      resultRows(results.volume),
      resultRows(results[`s_${safe}`]),
      resultRows(results[`n_${safe}`]),
      resultRows(results[`b_${safe}`]),
      resultRows(results[`v_${safe}`]),
    ),
    flowVersions: buildFlowVersions(
      resultRows(results.versions),
      versionIdToName,
    ),
  };
};
